import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { CACHE_DURATION } from "@consts";
import { gqlClient } from "src/services/graphql";
import { queryClient } from "src/lib/react-query/queryClient";
import useAppStore from "src/stores/appStore";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import {
  INTROSPECT_DOC,
  toGqlTypeName,
  toUpdateInputTypeName,
  buildBulkDeleteMutation,
  buildBulkReplaceMutation,
  buildBulkUpsertMutation,
  buildCreateMutation,
  buildDeleteMutation,
  buildExploreQuery,
  buildFieldValuesQuery,
  buildUpdateMutation,
  buildVersionsQuery
} from "src/queries/services/knowledgeExplore";

// The GraphQL endpoint scopes knowledge by the selected account/assistant via request
// headers, not query variables — so the assistant identity must be part of every explore
// cache key. Otherwise switching assistants serves the previous one's schema/versions/rows.
const useExploreScope = () => [
  useAppStore((s) => s.selectedAccountId),
  useAppStore((s) => s.selectedAssistant?.assistantId)
];

const getExploreScope = () => {
  const { selectedAccountId, selectedAssistant } = useAppStore.getState();
  return [selectedAccountId, selectedAssistant?.assistantId];
};

// Shared between the hooks and the imperative fetch* variants below so both hit
// the same cache entries.
const introspectionQueryOptions = (scope, schemaType) => ({
  queryKey: [FETCH_QUERY_KEYS.EXPLORE_INTROSPECTION, ...scope, schemaType],
  queryFn: async () =>
    await gqlClient.request(INTROSPECT_DOC, {
      name: toGqlTypeName(schemaType),
      updateInputName: toUpdateInputTypeName(schemaType)
    }),
  staleTime: Infinity,
  refetchOnWindowFocus: false
});

const versionsQueryOptions = (scope, schemaType) => ({
  queryKey: [FETCH_QUERY_KEYS.EXPLORE_VERSIONS, ...scope, schemaType],
  queryFn: async () => await gqlClient.request(buildVersionsQuery(schemaType)),
  staleTime: CACHE_DURATION.ONE_MINUTE,
  refetchOnWindowFocus: false
});

const exploreDataQueryOptions = (scope, { schemaType, version, fields, filters, pagination }) => ({
  queryKey: [FETCH_QUERY_KEYS.EXPLORE_DATA, ...scope, schemaType, version, filters, pagination, fields],
  queryFn: async () => await gqlClient.request(buildExploreQuery({ schemaType, fields }), { version, filters, pagination }),
  staleTime: CACHE_DURATION.ONE_MINUTE,
  refetchOnWindowFocus: false
});

// Strawberry camelizes GraphQL type names while query fields stay snake_case,
// so introspection must ask for the camelized type name (e.g. "siteInformation").
export const useSchemaIntrospection = (schemaType) => {
  const scope = useExploreScope();
  return useQuery({
    ...introspectionQueryOptions(scope, schemaType),
    enabled: Boolean(schemaType)
  });
};

export const useSchemaVersions = (schemaType) => {
  const scope = useExploreScope();
  return useQuery({
    ...versionsQueryOptions(scope, schemaType),
    enabled: Boolean(schemaType)
  });
};

export const useExploreData = ({ schemaType, version, fields, filters, pagination, enabled = true }) => {
  const scope = useExploreScope();
  return useQuery({
    ...exploreDataQueryOptions(scope, { schemaType, version, fields, filters, pagination }),
    placeholderData: keepPreviousData,
    enabled: Boolean(enabled && schemaType && version && fields?.length)
  });
};

// Imperative variants for code that runs outside the render cycle (bulk
// download/upload ref resolution): served from the cache while fresh, fetched
// otherwise.
export const fetchSchemaIntrospection = (schemaType) =>
  queryClient.fetchQuery(introspectionQueryOptions(getExploreScope(), schemaType));

export const fetchSchemaVersions = (schemaType) =>
  queryClient.fetchQuery(versionsQueryOptions(getExploreScope(), schemaType));

export const fetchExploreData = (params) =>
  queryClient.fetchQuery(exploreDataQueryOptions(getExploreScope(), params));

// Lazily fetches the distinct values of a [String] column for its multi-select
// picker. Cached per (scope, schemaType, field, version); only runs once `enabled`.
export const useExploreFieldValues = ({ schemaType, field, version, enabled }) => {
  const scope = useExploreScope();
  return useQuery({
    queryKey: [FETCH_QUERY_KEYS.EXPLORE_FIELD_VALUES, ...scope, schemaType, field, version],
    queryFn: async () => await gqlClient.request(buildFieldValuesQuery(schemaType), { field, version }),
    enabled: Boolean(enabled && schemaType && field && version),
    staleTime: CACHE_DURATION.ONE_MINUTE,
    refetchOnWindowFocus: false
  });
};

const invalidateExploreQueries = (schemaType) => {
  const scope = getExploreScope();
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_DATA, ...scope, schemaType] });
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_VERSIONS, ...scope, schemaType] });
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });
};

export const invalidateAllExploreQueries = () => {
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_DATA] });
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_VERSIONS] });
  queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.EXPLORE_FIELD_VALUES] });
};

export const useCreateRow = (schemaType) => useMutation({
  mutationFn: async ({ version, input }) =>
    await gqlClient.request(buildCreateMutation(schemaType), { version, input }),
  onSuccess: () => invalidateExploreQueries(schemaType)
});

export const useUpdateRow = (schemaType) => useMutation({
  mutationFn: async ({ version, id, input }) =>
    await gqlClient.request(buildUpdateMutation(schemaType), { version, id, input }),
  onSuccess: () => invalidateExploreQueries(schemaType)
});

export const useDeleteRow = (schemaType) => useMutation({
  mutationFn: async ({ version, id }) =>
    await gqlClient.request(buildDeleteMutation(schemaType), { version, id }),
  onSuccess: () => invalidateExploreQueries(schemaType)
});

export const useBulkDeleteRows = (schemaType) => useMutation({
  mutationFn: async ({ version, ids }) =>
    await gqlClient.request(buildBulkDeleteMutation(schemaType), { version, ids }),
  onSuccess: () => invalidateExploreQueries(schemaType)
});

export const useBulkUploadRows = (schemaType) => useMutation({
  mutationFn: async ({ version, inputs, replace }) =>
    await gqlClient.request(
      replace ? buildBulkReplaceMutation(schemaType) : buildBulkUpsertMutation(schemaType),
      { version, inputs }
    ),
  onSuccess: () => invalidateExploreQueries(schemaType)
});
